import * as Yup from 'yup'
import { maxCardLengthOptions, maxPossibleCardLength } from './utils'

export const mobileNumber = Yup.string()
  .required('Required')
  .min(10, 'Mobile number must be 10 digits')
  .max(10, 'Mobile number must be 10 digits')
  .matches(/^\d{10}$/, 'Only 10 digit mobile number allowed')
  .matches(/^[1-9][0-9]*$/, 'Mobile number must not start with 0')

export const numberValidation = Yup.string()
  .required('Required')
  .min(1, 'Please enter a number')
  .matches(/^[0-9]*$/, 'Only digits 0-9 are allowed.')
export const numberValidationNonZero = Yup.string()
  .required('Required')
  .min(1, 'Please enter a number')
  .matches(/^[0-9]*$/, 'Only digits 0-9 are allowed.')
  .test('is-positive-number', 'Value must be a number greater than 0', (value) => {
    // Check if the value is a number and greater than 0
    return value !== undefined && value !== null && !isNaN(value) && Number(value) > 0
  })

export const required = Yup.string()?.trim()?.required('Required!')
export const notRequired = Yup.string()?.trim().notRequired()
export const optionalAmountValidation = Yup.string()
  .matches(/^$|^\d+(\.\d{1,2})?$/, 'Please enter correct amount.')
  .nullable()

export const optionalCountValidation = Yup.string()
  .matches(/^[0-9]*$/, 'Only digits 0-9 are allowed.')
  .nullable()

export const checkBoxValidation = Yup.string().trim().required('Required').matches(/true/, 'Required')

export const optionalValidation = Yup.string()
  .matches(/^\d+(\.\d{1,2})?$/, 'Please enter correct amount.')
  .nullable()

export const Name = Yup.string()
  .trim()
  .min(2, 'Too Short Name!')
  .max(50, 'Too Long Name!')
  .required('Required')
  .matches(/^[a-zA-Z ]*$/, 'Only Alphabets are allowed.')

export const NameWithDot = Yup.string()
  .trim()
  .min(2, 'Too Short Name!')
  .max(50, 'Too Long Name!')
  .required('Required')
  .matches(/^[a-zA-Z]+(\s{0,1}[a-zA-Z'. ])*$/, 'Enter valid name.')

export const stateValidation = Yup.string()
  .trim()
  .min(4, 'Too Short State Name!')
  .max(50, 'Too Long State Name!')
  .required('Required')

export const cityValidation = Yup.string()
  .trim()
  .min(2, 'Too Short City Name!')
  .max(50, 'Too Long City Name!')
  .required('Required')
  .matches(/^[a-zA-Z.-]+(?:[\s-][/a-zA-Z.]+)*$/, 'Invalid city name')

export const email = Yup.string().trim().email('Invalid email').required('Required')

export const optionalEmail = Yup.string().trim().email('Invalid email').nullable()

export const otpSixDigit = Yup.string().trim().min(6, 'Enter correct OTP!').required('Required')

export const GSTValidation = Yup.string()
  .trim()
  .matches(/[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[1-9A-Z]{1}$/, 'Is not in correct format')
  .required()

export const GSTINValidation = Yup.string()
  .trim()
  .matches(/\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/, 'Invalid GSTIN')
  .required()

export const OptionalGSTINValidation = Yup.string()
  .trim()
  .matches(/\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/, 'Invalid GSTIN')
  .nullable()
  .optional()

export const panValidation = Yup.string()
  .trim()
  .matches(/[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Incorrect Pan Number')
  .required()
export const panOptionalValidation = Yup.string()
  .trim()
  .matches(/^([A-Z]{5}[0-9]{4}[A-Z]{1})?$/, 'Incorrect Pan Number')
  .optional()

export const aadharNumberValidation = Yup.string()
  .trim()
  .min(12, 'Enter correct Aadhar Number!')
  .required('Required')
  .matches(/^\d{12}$/, 'Incorrect Aadhar')

export const pincodeValidation = Yup.string()
  .trim()
  .required('Required')
  .min(6, 'Please enter 6 digit pincode')
  .matches(/^\d{6}$/, 'Incorrect pincode')

export const optionalPincodeValidation = Yup.string()
  .trim()
  .matches(/^\d{6}$/, 'Please enter 6 digit pincode')
  .nullable()
  .optional()

export const addressValidation = Yup.string()
  .trim()
  .required('Required')
  .min(10, 'Too Short Address!')
  .max(150, 'Please enter correct address!')

export const amountValidation = Yup.string()
  .trim()
  .required('Required')
  .min(1, 'Please enter a amount')
  .matches(/^\d+(\.\d{1,2})?$/, 'Please enter correct amount.')

export const maximumAmountValidation = Yup.string()
  .trim()
  .required('Required')
  .max(6)
  .test('Validate decimal number', 'Decimal values are not allowed', (amount: string) => {
    return amount?.split('.')?.length < 2
  })
  .test('Test if its less than 1 lakh', 'Amount should be less than 1 lakh', (amount: string) => {
    return parseFloat(amount) <= 100000
  })
  .test('Validate number', 'Please enter valid amount', (amount: string) => {
    return /^[0-9]+$/.test(amount)
  })

export const maximumAmountObjectValidation = Yup.object().shape({
  maxAmount: Yup.string()
    .trim()
    .required('Required.')
    .test('Validate number', 'Please enter correct amount.', (amount: string) => {
      return /^\d+(\.\d{1,2})?$/.test(amount)
    })
    .when('minAmount', (minAmount, schema) => {
      return minAmount && minAmount > 0 && schema.min(parseFloat(minAmount) && schema.max(100000))
    }),
})

export const bankAccountValidation = Yup.string()
  .trim()
  .matches(/^\d{9,16}$/, 'Incorrect Account Number')
  .required('Account Number is a required field')

export const bankIFSCValidation = Yup.string()
  .trim()
  .matches(/^[A-Z]{4}[A-Z0-9]{7}$/, 'Incorrect IFSC code')
  .required('IFSC is a required field')

export const alphaNumericValidation = Yup.string()
  .max(20, 'Max allowed length is 20')
  .matches(/(^$)|(^[a-zA-Z0-9]+$)/, 'Only alpha-numeric characters are allowed')

let prevExpLength = 0
export const expiryDateValidationText = (expiryWithoutSpace: string) => {
  // // to detect cases of deletion
  let formattedExpiry = ''
  let index = 0
  if (expiryWithoutSpace.length === 1 && parseInt(expiryWithoutSpace, 10) > 1) {
    expiryWithoutSpace = '0' + expiryWithoutSpace
  }
  if (expiryWithoutSpace.length === 2 && parseInt(expiryWithoutSpace, 10) > 12) {
    expiryWithoutSpace = '0' + expiryWithoutSpace
  }
  while (index < expiryWithoutSpace.length) {
    formattedExpiry += expiryWithoutSpace.charAt(index)
    if (index !== 0 && prevExpLength !== expiryWithoutSpace.length && index === 1) {
      formattedExpiry += ' / '
    }
    index += 1
  }
  prevExpLength = expiryWithoutSpace.length
  return formattedExpiry
}

export const isValidCardNumber = (valueWithSpace: string) => {
  const value = valueWithSpace?.replace(/[^0-9]/g, '')
  let nCheck = 0
  let bEven = false

  if (value) {
    for (let n = value.length - 1; n >= 0; n -= 1) {
      const cDigit = value.charAt(n)
      let nDigit = parseInt(cDigit, 10)

      if (bEven) {
        nDigit *= 2
        if (nDigit > 9) {
          nDigit -= 9
        }
      }

      nCheck += nDigit
      bEven = !bEven
    }
  }
  return nCheck % 10 === 0
}

export const getCardValidatonError = (value: string, cardVendor: string): string => {
  if (cardVendor === 'none') {
    return 'Please enter a valid card number'
  }
  if (cardVendor in maxPossibleCardLength) {
    if (!maxPossibleCardLength[cardVendor as maxCardLengthOptions].includes(value?.length ?? '')) {
      return 'Incomplete card number'
    }
  } else {
    return 'Please enter a valid card number'
  }
  return 'Please enter a valid card number'
}

export const checkValidCard = (value: string, cardVendor: string): boolean => {
  if (cardVendor === 'none') {
    return false
  }
  if (cardVendor in maxPossibleCardLength) {
    if (!maxPossibleCardLength[cardVendor as maxCardLengthOptions].includes(value?.length ?? '')) {
      return false
    }
  } else {
    console.log('checkValidCard value1', value, cardVendor)
    return false
  }
  return true
}

export const getExpiryMessage = (data: string): string => {
  const monthDate = data.replace(' / ', '/')
  const monthStr = monthDate.split('/')[0]
  const yearStr = monthDate.split('/')[1]
  const month = Number(monthStr)
  const year = Number(`20${yearStr}`)
  const today = new Date()
  const MAX_EXPIRY_YEARS = 10
  if (isNaN(month) || isNaN(year)) {
    return 'Invalid Expiry Date'
  }
  if (month > 12 && month < 1) {
    return 'Invalid Expiration month'
  }
  if (!(year > today.getFullYear() || (year === today.getFullYear() && month >= today.getMonth() + 1))) {
    return 'expiration year is in the past'
  }
  if (!(today.getFullYear() + MAX_EXPIRY_YEARS > year)) {
    return 'Invalid Expiry Date'
  }

  return ''
}

export const cardNumber = Yup.string()
  .min(12, 'Minimum 12 digits required')
  .required('Required')
  .test('Test if its a valid', 'Please enter a valid credit/debit card number', (value: string) => {
    return isValidCardNumber(value)
  })

export const cvvSchema = Yup.string().min(3, 'Minimum 3 digits required').required('Required')

export const TwoInputSchema = (field1: string, field2: string) =>
  Yup.string()
    .required('Required')
    .test('Test if its a valid', `Enter both ${field1} and ${field2}`, (value: any) => {
      const splitValues = value?.split('_') ?? []
      if (splitValues.length === 1) {
        return splitValues[0] && splitValues[0] !== '0'
      }

      return splitValues[0] && splitValues[0] !== '0' && splitValues[1] && splitValues[1] !== '0'
    })

export const expiry = Yup.string()
  .min(4, 'Enter valid 4 digits')
  .required('Required')
  .matches(/^[0-1][0-9]\s*\/\s*[0-9][0-9]$/, 'Invalid Date (mm/yy). Starting digit must be 0 or 1 eg: 09/19')
  .test('Test if its a valid', 'Month should be less than or equal to 12 and greater than 0', (value: string) => {
    if (!value) {
      return false
    }
    const monthStr = value.split('/')[0]?.trim()
    const month = Number(monthStr)
    if (isNaN(month)) {
      return false
    }
    const ret = month < 13 && month > 0
    return ret
  })
  .test('Test if its a future date', 'Expiry date should be future date', (value: string) => {
    if (!value) {
      return false
    }
    const monthStr = value.split('/')[0]?.trim()
    const yearStr = value.split('/')[1]?.trim()
    if (!monthStr || !yearStr) {
      return false
    }
    const month = Number(monthStr)
    const year = Number(`20${yearStr}`)
    if (isNaN(month) || isNaN(year)) {
      return false
    }
    const today = new Date()

    return year > today.getFullYear() || (year === today.getFullYear() && month >= today.getMonth() + 1)
  })
  .test('Test if its a future date', 'Expiry date should be less than 10 years', (value: string) => {
    if (!value) {
      return false
    }
    const yearStr = value.split('/')[1]?.trim()
    if (!yearStr) {
      return false
    }
    const year = Number(`20${yearStr}`)
    if (isNaN(year)) {
      return false
    }
    const today = new Date()
    const MAX_EXPIRY_YEARS = 10
    return today.getFullYear() + MAX_EXPIRY_YEARS > year ? true : false
  })

export const DOB = Yup.string()
  .required('Required')
  .matches(/^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/, 'Invalid Date of birth ')

export const Password = Yup.string()
  .required('Required')
  .matches(
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/,
    'Password should contain at least one lowercase letter, one uppercase letter, one numeric digit, and one special character',
  )

export const validateArrayLength = Yup.string().required('Required')
